/**
 * RequestForm Component
 * Form for creating or editing a request with flexible date selection modes
 */

import React from 'react';
import {
    Request,
    RequestType,
    CreateRequestDto,
    UpdateRequestDto,
} from '@features/requests/types';
import { Shift } from '../../types/dateSelectionTypes';
import { useRequestForm } from './hooks';
import {
    RequestTypeField,
    RequestInfoCard,
    DateSelectionModeField,
    DateSelectionField,
    NotesField,
    FormFooter,
} from './components';
import { useI18n } from '@shared/i18n';
import type { SingleDateValue } from '@dynamon-as/ui';
import styles from './RequestForm.module.css';

// Note: MAX_ATTACHMENTS and MAX_ATTACHMENT_SIZE are used in AttachmentsInfo component
// but imported here for potential future use in this file

/**
 * Props for RequestForm component
 */
export interface RequestFormProps {
    /** Initial values for the form (for editing) */
    initialValues?: Partial<Request>;
    /** Callback when form is submitted (Save as Draft or Update) */
    onSubmit: (dto: CreateRequestDto | UpdateRequestDto) => void;
    /** Callback when user cancels */
    onCancel: () => void;
    /** Whether the form is for creating a new request */
    isCreate?: boolean;
    /** Callback for Submit action (Send inn) - only for create mode */
    onSubmitAction?: (dto: CreateRequestDto | UpdateRequestDto) => void;
    /** Available shifts for shift selection mode */
    availableShifts?: Shift[];
    /** Initial excluded dates that the employee has excluded from the pattern */
    initialExcludedDates?: Date[];
}

/**
 * Form for creating or editing a request
 */
export function RequestForm({
    initialValues,
    onSubmit,
    onCancel,
    isCreate = true,
    onSubmitAction,
    availableShifts,
    initialExcludedDates,
}: RequestFormProps): React.ReactElement {
    const { t } = useI18n();
    const {
        // State
        type,
        notes,
        dateSelectionMode,
        errors,
        shiftPreference,
        excludedDates,

        // Handlers
        setType,
        setNotes,
        setShiftPreference,
        setExcludedDates,
        handleDateSelectionModeChange,
        handleCalendarChange,

        // Actions
        validateForm,
        buildDto,
        getCalendarProps,
    } = useRequestForm({
        initialValues,
        availableShifts,
        excludedDates: initialExcludedDates,
    });

    // Date constraints
    // For editing existing requests (drafts), allow from today onwards
    // For new requests, allow from tomorrow onwards
    // Note: deadline does NOT block calendar selection, only validates on submit
    const minDate = isCreate
        ? ((): Date => {
              const tomorrow = new Date();
              tomorrow.setDate(tomorrow.getDate() + 1);
              tomorrow.setHours(0, 0, 0, 0);
              return tomorrow;
          })()
        : ((): Date => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              return today;
          })();

    // No max date constraint in calendar - deadline is validated on submit only
    const maxDate = undefined;

    // Get calendar props with proper typing
    const calendarProps = getCalendarProps();
    // The value can be SingleDateValue, RangeDateValue, MultipleDateValue, or ShiftDateValue
    // For DateSelectionField, we need to handle all these cases
    const calendarValue: SingleDateValue | null =
        calendarProps.value as SingleDateValue | null;

    // Handle form submission
    const handleSubmit = (e: React.FormEvent): void => {
        e.preventDefault();
        if (!validateForm()) {
            return;
        }
        onSubmit(buildDto());
    };

    return (
        <form
            className={styles.container}
            onSubmit={handleSubmit}
        >
            <div className={styles.header}>
                <h2
                    id="form-title"
                    className={styles.title}
                >
                    {isCreate
                        ? t('requests.form.newRequest')
                        : t('requests.form.editRequest')}
                </h2>
            </div>

            <div className={styles.content}>
                {isCreate ? (
                    <>
                        {/* Request Type */}
                        <RequestTypeField
                            value={type}
                            onChange={setType}
                            error={errors.type}
                            disabled={!isCreate}
                        />

                        {/* Date Selection Mode */}
                        <DateSelectionModeField
                            value={dateSelectionMode}
                            onChange={handleDateSelectionModeChange}
                            disabled={!isCreate}
                            disabledModes={
                                type !== RequestType.SHIFT_PREFERENCE
                                    ? ['shift']
                                    : undefined
                            }
                            requestType={type}
                        />
                    </>
                ) : (
                    /* Read-only summary: type, deadline and status */
                    <RequestInfoCard request={initialValues ?? {}} />
                )}

                {/* Date Selection (calendar) - editable in both modes */}
                <DateSelectionField
                    mode={dateSelectionMode}
                    selectionType={calendarProps.selectionType}
                    value={calendarValue}
                    onChange={handleCalendarChange}
                    shiftPreference={shiftPreference}
                    onShiftSelect={setShiftPreference}
                    errors={errors}
                    minDate={minDate}
                    maxDate={maxDate}
                    disabledDates={[]}
                    excludedDates={excludedDates}
                    onExcludedDatesChange={setExcludedDates}
                    requestType={type}
                />

                {/* Notes */}
                <NotesField
                    value={notes}
                    onChange={setNotes}
                />

                {/* Attachments Info - NOTE: hidden for now
                    <AttachmentsInfo />
                */}
            </div>

            <FormFooter
                isCreate={isCreate}
                onCancel={onCancel}
                onSubmit={onSubmit}
                onSubmitAction={onSubmitAction}
                validateForm={validateForm}
                buildDto={buildDto}
            />
        </form>
    );
}

export default RequestForm;
